import {
  StripeElements,
  StripeError,
  Stripe,
  CustomPaymentMethod,
  ConfirmationToken,
  Appearance,
  ExpressPaymentType,
  ShippingRate,
  DeliveryEstimate,
  ExpressCheckoutPartialAddress,
  ExpressCheckoutPaymentMethodsOption,
} from "@stripe/stripe-js";
import { ApplePayOption } from "@stripe/stripe-js/dist/stripe-js/elements/apple-pay";
import intlService, { locales } from "../../../services/intl";
import { captureError } from "../../../utils/error";

import Cart, { BillingDetails } from "../../../store/cart/model";
import fetchInternal from "../../../utils/fetch";
import { Color } from "../../../utils/styleDesignSystem";
import { capitalize } from "../../../utils/style";
import CartProduct from "../../../store/cart-product/model";
import { getStore } from "../../../store/createStore";
import {
  getProductSkuForPlanId,
  getProductForSku,
} from "../../../utils/planToProduct";
import { productOfferForId } from "../../../store/product-offer/selectors";
import Coupon from "../../../store/coupon/model";
import {
  CartAddressPayload,
  CompleteStripeElementsUpdateOptions,
  TruemedPaymentSession,
  APIError,
  Result,
  ExpressCheckoutVariationCohort,
  PaymentMethod,
  ExpressCheckoutLineItem,
  BaseAPIPurchase,
  BaseAPIError,
} from "../types";

export const tryElementsSubmit = async (
  elements: StripeElements,
): Promise<{
  success: boolean;
  error: StripeError | undefined;
  selectedPaymentMethod: string | undefined;
}> => {
  const { error, selectedPaymentMethod } = await elements.submit();

  const success = !error;

  return { success, error, selectedPaymentMethod };
};

export const tryCreateConfirmationToken = async (
  stripe: Stripe,
  elements: StripeElements,
) => {
  const { confirmationToken, error } = await stripe.createConfirmationToken({
    elements,
  });

  return { confirmationToken, error };
};
export const tryApiPurchase = async ({
  confirmationToken,
  cartId,
  subscriptionConsentGiven = false,
}: {
  confirmationToken: ConfirmationToken;
  cartId: string;
  subscriptionConsentGiven: boolean;
}): Promise<{
  response?: BaseAPIPurchase;
  error?: BaseAPIError;
}> => {
  try {
    const response = await fetchInternal("purchase", {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      fetchOptions: {
        addCastleRequestToken: true,
      },
      body: JSON.stringify({
        confirmation_token: confirmationToken.id,
        cart_id: cartId,
        subscription_consent_given: subscriptionConsentGiven,
      }),
    });

    return { response };
  } catch (error) {
    return { error: error as BaseAPIError };
  }
};

export const tryHandleNextAction = async (
  clientSecret: string,
  stripe: Stripe,
) => {
  const { error, paymentIntent, setupIntent } = await stripe.handleNextAction({
    clientSecret,
  });

  return { paymentIntent, setupIntent, error };
};

export const finalizePurchase = async (cartId: string) => {
  await fetchInternal(`purchase/finalize`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    fetchOptions: {
      addCastleRequestToken: true,
    },
    body: JSON.stringify({
      cart_id: cartId,
    }),
  });
};

export const getStripeElementsAppearanceVariables = (
  overrideVariables?: Partial<Appearance["variables"]>,
): Appearance["variables"] => {
  return {
    borderRadius: "4px",
    colorDanger: `${Color.errorRed}`,
    colorPrimary: `${Color.indigoBlue}`,
    colorSuccess: `${Color.successGreen}`,
    colorText: `${Color.indigoBlue}`,
    colorWarning: `${Color.warningOrange}`,
    fontFamily: "CircularXX",
    fontSizeBase: "16px",
    fontVariantLigatures: "normal",
    fontWeightBold: "500",
    fontWeightLight: "450",
    fontWeightMedium: "500",
    fontWeightNormal: "450",
    iconHoverColor: `${Color.indigoBlue40}`,
    iconChevronDownColor: `${Color.indigoBlue}`,
    iconChevronDownHoverColor: `${Color.indigoBlue40}`,
    iconCloseColor: `${Color.indigoBlue}`,
    iconCloseHoverColor: `${Color.indigoBlue40}`,
    iconColor: `${Color.indigoBlue}`,
    colorTextPlaceholder: `${Color.indigoBlue40}`,
    ...overrideVariables,
  };
};

export const stripeAppearanceConfig: Appearance = {
  theme: "stripe",
  labels: "floating",
  variables: getStripeElementsAppearanceVariables(),
  rules: {
    ".Input": {
      boxShadow: "none",
    },
    ".AccordionItem": {
      boxShadow: "none",
    },
  },
};

export const stripeElementsConfig = (
  cart: Cart,
  recurringItems: boolean,
  localeId?: string,
  truemedEnabled = false,
): CompleteStripeElementsUpdateOptions => {
  const truemedPaymentMethodId = process.env.GATSBY_TRUEMED_PAYMENT_METHOD_ID;

  const cartTotal = cart?.total || 0;

  const showTruemed =
    truemedEnabled &&
    localeId === "en-US" &&
    truemedPaymentMethodId &&
    cartTotal > 0;

  const customPaymentMethods: CustomPaymentMethod[] = showTruemed
    ? [
        {
          id: truemedPaymentMethodId,
          options: { type: "static", subtitle: "Pay with HSA/FSA" },
        },
      ]
    : [];

  return {
    mode: cart.requiresPaymentIntent ? "payment" : "setup",
    amount: cart.requiresPaymentIntent ? cart.total : undefined,
    currency: cart.currency,
    ...(recurringItems && { setupFutureUsage: "off_session" }),
    appearance: stripeAppearanceConfig,
    customPaymentMethods,
  };
};

export const patchCart = async (payload: CartAddressPayload, cart: Cart) => {
  const response = await fetchInternal(`carts/${cart.id}`, {
    method: "PATCH",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    fetchOptions: {
      addCastleRequestToken: true,
    },
    body: JSON.stringify(payload),
  });

  return response;
};

export const setLocalStorage = (purchase: {
  first_name?: string;
  last_name?: string;
  order_number?: string;
  reset_password_token?: string;
  payment_methods?: string[];
}) => {
  const keys = [
    "first_name",
    "last_name",
    "order_number",
    "reset_password_token",
  ] as const;
  keys.forEach((key) => {
    if (purchase[key]) {
      sessionStorage.setItem(`rit-${key}`, purchase[key] as string);
    }
  });
};

export const getApplePayOption = (
  activeCart: Cart,
): ApplePayOption | undefined => {
  const recurringBillingDetails = activeCart?.recurringBillingDetails;

  if (!recurringBillingDetails) return;
  if (Object.keys(recurringBillingDetails).length !== 1) return;

  const cadence = Object.keys(recurringBillingDetails)[0] || "30";
  const details = recurringBillingDetails[cadence] as BillingDetails;

  return {
    recurringPaymentRequest: {
      paymentDescription: "My Ritual Subscription",
      managementURL: "https://account.ritual.com/",
      regularBilling: {
        amount: details.amount,
        label: `Recurring subscription (${
          activeCart.taxInclusive ? "VAT Included" : "plus tax"
        })`,
        recurringPaymentIntervalUnit: "day",
        recurringPaymentIntervalCount: Number(cadence),
        recurringPaymentStartDate: new Date(
          new Date().setDate(Number(new Date().getDate() + cadence)),
        ),
      },
      billingAgreement: `Some of the items you are purchasing will automatically renew as a subscription and will be billed every ${cadence} days until you cancel. You can cancel anytime by visiting your account page on our website. By clicking Pay, you confirm that you have read, understand, and agree to the Terms of Service and Privacy Policy.`,
    },
  };
};

export const createTruemedPaymentSession = async (
  cartId: string,
  name: string,
  email: string,
): Promise<Result<TruemedPaymentSession, APIError>> => {
  try {
    const response = await fetchInternal("truemed/purchase", {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      fetchOptions: {
        addCastleRequestToken: true,
      },
      body: JSON.stringify({ cart_id: cartId, name, email }),
    });

    return { success: true, data: response as TruemedPaymentSession };
  } catch (error) {
    return { success: false, error: error as APIError };
  }
};

export const finalizeTruemedPayment = async (
  cartId: string,
): Promise<Result<{}, APIError>> => {
  try {
    await fetchInternal(
      "truemed/purchase/finalize",
      {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        fetchOptions: {
          addCastleRequestToken: true,
        },
        body: JSON.stringify({ cart_id: cartId }),
      },
      22,
      500,
    );
    return { success: true, data: {} };
  } catch (error) {
    return { success: false, error: error as APIError };
  }
};

export const parsePaymentMethod = (
  paymentMethod?: PaymentMethod,
): string | undefined => {
  if (!paymentMethod) return;
  const { truemed, card, type } = paymentMethod;
  if (truemed) {
    return "Truemed";
  }

  if (card) {
    const { brand, last4 } = card;
    return `${capitalize(brand)} ${last4}`;
  }

  if (!type) return;

  return capitalize(type.replace("_", " "));
};

export const getShippingRates = (
  expressPaymentType: ExpressPaymentType,
  shippingAmount: number,
): ShippingRate[] => {
  const localeConfig = locales[intlService.locale]!;

  const deliveryEstimate =
    expressPaymentType === "apple_pay"
      ? {
          maximum: {
            unit: "day",
            value: localeConfig.deliveryEstimate[1],
          } as DeliveryEstimate,
          minimum: {
            unit: "day",
            value: localeConfig.deliveryEstimate[0],
          } as DeliveryEstimate,
        }
      : localeConfig.deliveryEstimate[1].toString();

  const rate =
    shippingAmount && shippingAmount > 0
      ? {
          id: "shipping",
          displayName: "Shipping",
          amount: shippingAmount,
          deliveryEstimate,
        }
      : {
          id: "free-shipping",
          displayName: "Free shipping",
          amount: 0,
          deliveryEstimate,
        };
  return [rate];
};

export const getExpressCheckoutPaymentMethods = (
  cohort: ExpressCheckoutVariationCohort,
): ExpressCheckoutPaymentMethodsOption => {
  const defaultExpressCheckoutPaymentMethods: ExpressCheckoutPaymentMethodsOption =
    {
      amazonPay: "never",
      applePay: "never",
      googlePay: "never",
      link: "never",
      paypal: "never",
    };

  if (!cohort || cohort === "none") return defaultExpressCheckoutPaymentMethods;

  return cohort.split("-").reduce((acc, method) => {
    return { ...acc, [method]: "auto" };
  }, defaultExpressCheckoutPaymentMethods);
};

/**
 * Patch the cart with the updated shipping address and coupon code.
 * Returns line items to be passed back to the express checkout element.
 */
export const onExpressCheckoutShippingAddressChange = async (
  cart: Cart,
  cartProducts: CartProduct[],
  address: ExpressCheckoutPartialAddress,
  coupon?: Coupon,
) => {
  let payload: CartAddressPayload = {
    shipping_address: {
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      country: address.country,
    },
    discount_code: coupon ? coupon.code : null,
  };
  const response = await patchCart(payload, cart);
  const updatedCart = new Cart({});
  updatedCart.deserialize(response.data);

  sessionStorage.setItem(
    "rit-purchased_cart",
    JSON.stringify(response.data.attributes),
  );

  const updatedLineItems = getExpressCheckoutLineItems(
    cartProducts,
    updatedCart,
  );

  return {
    updatedLineItems,
    amount: updatedCart.total,
    currency: updatedCart.currency,
  };
};

export const getPurchasedCart = () => {
  const cart = sessionStorage.getItem("rit-purchased_cart");
  const cartId = sessionStorage.getItem("rit-cart_id");
  if (!cart) {
    captureError(
      { error: "rit-purchased_cart not found" },
      "Purchased cart not found in checkout utils",
    );
    return {};
  }
  try {
    const parsedCart = JSON.parse(cart);
    if (!parsedCart.id) parsedCart.id = cartId;
    return parsedCart;
  } catch (error) {
    captureError(error as Error, "Failed to parse cart JSON");
    return {};
  }
};

export const getExpressCheckoutLineItems = (
  cartProducts: CartProduct[],
  cart: Cart,
): ExpressCheckoutLineItem[] => {
  const state = getStore().getState();
  const lineItems = cartProducts.map((cartProduct) => {
    const { quantity, planId, productOfferId, productPrice } = cartProduct;
    let name = "";

    if (productOfferId) {
      const productOffer = productOfferForId(state, productOfferId);
      name = productOffer.name;
    } else {
      const productSku = getProductSkuForPlanId(planId);
      const product = getProductForSku(productSku);
      name = product.name;
    }

    return {
      name: `${quantity}x ${name}`,
      amount: productPrice * quantity,
    };
  });

  if (cart.discountAmount > 0) {
    lineItems.push({
      name: "Discount",
      amount: -cart.discountAmount,
    });
  }

  lineItems.push({
    name: cart.taxInclusive ? "VAT Included" : "Tax",
    amount: cart.totalTax || 0,
  });

  // if cart.shippingAmount is not null, add shipping line item
  if (cart.shippingAmount > 0) {
    lineItems.push({
      name: "Shipping",
      amount: cart.shippingAmount,
    });
  }

  return lineItems;
};

export const getAppliedPromotion = () => {
  try {
    const promotion = sessionStorage.getItem("rit-applied_promotion");
    if (!promotion || promotion === "undefined") {
      return null;
    }
    return JSON.parse(promotion);
  } catch (error) {
    captureError(
      error as Error,
      "Invalid data in sessionStorage for applied promotion",
    );
    return null;
  }
};
